import { useCart } from 'contexts/CartContext/CartContext.hooks';
import { IUseLineEdit } from './LineEditContext.types';
import {
    DefaultCostSummaryItem,
    IByod,
    ICostSummaryItem,
    IDeviceItem,
    ILineItem
} from 'contexts/CartContext/CartContext.types';
import { useContext } from 'react';
import { LineEditContext } from './LineEditContext';
import { IPlanSelection } from '../../pages/Plans/PlanPage.types';
import { DiscountType } from '../../types/Promo';
import { IDevice } from '../../types/Device';
import { usePlansHooks } from '../../pages/Plans/PlansHooks';
import { IPlanSku } from '../../types/PlanSku';

export const useLineEdit = (): IUseLineEdit => {
    const { current, setCurrent } = useContext(LineEditContext);

    const { cart, updateLineItems } = useCart();

    const { GetPlanSku } = usePlansHooks();

    const getLineItems = (): ILineItem[] => {
        return current < cart.lineItems.length
            ? [...cart.lineItems]
            : [
                  {
                      lineNumber: current + 1,
                      phoneNumberInformation: { needsNewNumber: false },
                      isPrimaryLine: current === 0
                  }
              ];
    };

    const getCurrentLineItem = (): ILineItem => {
        return cart.lineItems[current];
    };

    const setByod = (item: IByod) => {
        const lineItems = getLineItems();

        lineItems[current].byod = item;

        updateLineItems([...lineItems]);
    };

    const setDevice = (item: IDeviceItem) => {
        const lineItems = getLineItems();

        lineItems[current].device = item;

        if (currentLineHasPlan()) {
            setPlanSku();
        } else {
            updateLineItems([...lineItems]);
        }
    };

    const setPlan = (item: IPlanSelection) => {
        const lineItems = getLineItems();

        lineItems[current].planSelection = item;

        if (currentLineHasDevice()) {
            setPlanSku();
        } else {
            updateLineItems([...lineItems]);
        }
    };

    const setPlanSku = () => {
        const currentDevice = GetCurrentDevice();
        const currentLineItem = getCurrentLineItem();

        if (currentLineItem.planSelection && currentDevice) {
            GetPlanSku(
                currentLineItem.planSelection.plan.id,
                currentDevice.deviceType,
                currentLineItem.lineNumber,
                (planSku: IPlanSku) => {
                    const lineItems = getLineItems();
                    lineItems[current].planSelection!.planSku = planSku;
                    updateLineItems([...lineItems]);
                },
                () => console.log('plan sku not found')
            );
        }
    };

    const removeLine = (lineNumber: number) => {
        const lineCart = cart.lineItems.filter((x) => x.lineNumber != lineNumber);
        updateLineItems(lineCart);
    };

    const setLines = (lines: ILineItem[]) => {
        updateLineItems(lines);
        //console.log(`LineEditContext.hook.setLines: setCurrent(0)`);
        setCurrent(0);
    };

    const currentLineHasDevice = (): boolean => {
        return (
            cart.lineItems[current]?.device !== undefined ||
            cart.lineItems[current]?.byod !== undefined
        );
    };

    const atLeastOneLineHasNoDevice = (): boolean => {
        return cart.lineItems.some((lineItem) => {
            return lineItem.device === undefined && lineItem.byod === undefined;
        });
    };

    const findIndexOfLineWithNoDevice = (): number => {
        return cart.lineItems.findIndex(
            (lineItem) => lineItem.device === undefined && lineItem.byod === undefined
        );
    };

    const currentLineHasPlan = (): boolean => {
        return cart.lineItems[current]?.planSelection !== undefined;
    };

    const CalculateDiscount = (
        price: number,
        type: DiscountType,
        discountAmount: number
    ): string => {
        switch (type) {
            case DiscountType.FlatAmount:
                return discountAmount.toFixed(2);
            case DiscountType.Percent:
                return ((discountAmount / 100) * price).toFixed(2);
        }
    };

    const HandleMoveLine = (lineNumber: number, targetLine: number) => {
        const line = cart.lineItems.splice(lineNumber - 1, 1);
        cart.lineItems.splice(targetLine - 1, 0, line[0]);

        setLines(cart.lineItems);
    };

    const TotalLines = (): number => {
        return cart.lineItems.length;
    };

    const GetCurrentDevice = (): IDevice | undefined => {
        const device = cart.lineItems[current].device?.product as IDevice;
        if (device) return device;
    };

    const GetMonthlyTotal = (lineNumber: number): ICostSummaryItem => {
        if (cart.monthlySummary) {
            let totalCosts = 0;

            const summariesForLine = cart.monthlySummary.filter((m) => m.lineNumber === lineNumber);

            const children = summariesForLine.filter((m) => m.type === 'cost');

            summariesForLine
                .filter((m) => m.type === 'cost')
                .forEach((c) => (totalCosts += c.amount));

            const retVal: ICostSummaryItem = {
                lineNumber: lineNumber,
                itemName: `Line ${lineNumber}`,
                amount: totalCosts,
                type: 'display',
                children: []
            };

            retVal.children = children;

            return retVal;
        }
        return DefaultCostSummaryItem();
    };

    const GetDueTodayTotal = (lineNumber: number): ICostSummaryItem => {
        if (cart.dueTodaySummary) {
            let totalCosts = 0;

            const summariesForLine = cart.dueTodaySummary.filter(
                (m) => m.lineNumber === lineNumber
            );

            const children = summariesForLine.filter((m) => m.type === 'cost');

            summariesForLine
                .filter((m) => m.type === 'cost')
                .forEach((c) => (totalCosts += c.amount));

            const retVal: ICostSummaryItem = {
                lineNumber: lineNumber,
                itemName: `Line ${lineNumber}`,
                amount: totalCosts,
                type: 'display',
                children: []
            };

            retVal.children = children;

            return retVal;
        }
        return DefaultCostSummaryItem();
    };

    const GetAdditionalCostItems = (): ICostSummaryItem[] => {
        if (cart.dueTodaySummary) {
            return cart.dueTodaySummary.filter((t) => t.lineNumber === 0);
        }

        return [];
    };

    return {
        current,
        setCurrent,
        getLineItems,
        getCurrentLineItem,
        setByod,
        setDevice,
        setPlan,
        removeLine,
        setLines,
        currentLineHasDevice,
        atLeastOneLineHasNoDevice,
        findIndexOfLineWithNoDevice,
        currentLineHasPlan,
        CalculateDiscount,
        HandleMoveLine,
        TotalLines,
        GetCurrentDevice,
        GetMonthlyTotal,
        GetDueTodayTotal,
        GetAdditionalCostItems
    };
};
